/**
 * TileMapShader - Compiled shader programs for tile map rendering
 *
 * Owns the two-pass shader programs:
 * - Sprite pass: Renders sprites to offscreen buffer at 1x scale
 * - Compose pass: Samples offscreen with pixel-art AA and applies view transform
 *
 * This class is designed to be shared between multiple TileMapRenderer instances.
 */

// Vertex shader for sprite pass - renders at 1x scale to offscreen buffer
const SPRITE_VERTEX_SHADER = `#version 300 es
  precision highp float;

  in vec2 a_position;      // Quad vertex position (0-1)
  in vec2 a_tilePos;       // Tile grid position (instanced)
  in vec4 a_spriteUV;      // Sprite UV rect: x, y, w, h in pixels (instanced)
  in vec4 a_fgColor;       // Foreground color RGBA (instanced)
  in vec4 a_detailColor;   // Detail color RGBA (instanced)
  in float a_atlasLayer;   // Atlas layer index (instanced)

  uniform vec2 u_resolution;     // Offscreen buffer size in pixels
  uniform vec2 u_tileSize;       // Tile size in pixels (16x24)
  uniform vec2 u_tileOffset;     // First visible tile coordinates
  uniform vec2 u_atlasArraySize; // Texture array dimensions
  uniform vec2 u_pixelOffset;    // Pixel offset for padding

  out vec3 v_texCoord;     // xyz = uvw (w is layer)
  out vec4 v_fgColor;
  out vec4 v_detailColor;

  void main() {
    // Calculate position relative to first visible tile (integer pixel positions)
    // Add pixel offset to account for padding border
    vec2 relativeTilePos = a_tilePos - u_tileOffset;
    vec2 pixelPos = relativeTilePos * u_tileSize + a_position * u_tileSize + u_pixelOffset;

    // Convert to clip space (-1 to 1)
    vec2 clipPos = (pixelPos / u_resolution) * 2.0 - 1.0;
    clipPos.y = -clipPos.y; // Flip Y for canvas coordinates

    gl_Position = vec4(clipPos, 0.0, 1.0);

    // Calculate texture coordinates
    vec2 pixelUV = a_spriteUV.xy + vec2(a_position.x, 1.0 - a_position.y) * a_spriteUV.zw;
    v_texCoord = vec3(pixelUV / u_atlasArraySize, a_atlasLayer);
    v_fgColor = a_fgColor;
    v_detailColor = a_detailColor;
  }
`;

// Fragment shader for sprite pass - direct sampling, no AA (NEAREST handles it)
const SPRITE_FRAGMENT_SHADER = `#version 300 es
  precision highp float;
  precision highp sampler2DArray;

  uniform sampler2DArray u_atlasArray;

  in vec3 v_texCoord;      // xy = uv, z = layer
  in vec4 v_fgColor;
  in vec4 v_detailColor;

  out vec4 fragColor;

  void main() {
    // Direct texture sampling - NEAREST filtering handles pixel-perfect sampling
    vec4 texColor = texture(u_atlasArray, v_texCoord);

    // Binary sprite colorization (ShaderMode 0)
    float brightness = texColor.r;
    vec4 finalColor = mix(v_fgColor, v_detailColor, brightness);

    // Final alpha: texture alpha * interpolated color alpha
    float alpha = texColor.a * finalColor.a;

    // Discard fully transparent pixels
    if (alpha < 0.01) discard;

    fragColor = vec4(finalColor.rgb, alpha);
  }
`;

// Vertex shader for composition pass - full screen quad
const COMPOSE_VERTEX_SHADER = `#version 300 es
  precision highp float;

  in vec2 a_position; // 0-1 quad

  out vec2 v_screenPos;  // Screen position in pixels

  void main() {
    // Full-screen quad: a_position (0,0) -> top-left, (1,1) -> bottom-right
    vec2 clipPos = a_position * 2.0 - 1.0;
    clipPos.y = -clipPos.y;
    gl_Position = vec4(clipPos, 0.0, 1.0);

    // Pass screen position (0,0 is top-left)
    v_screenPos = a_position;
  }
`;

// Fragment shader for composition pass - pixel-art AA sampling
const COMPOSE_FRAGMENT_SHADER = `#version 300 es
  precision highp float;

  uniform sampler2D u_offscreenTex;
  uniform vec2 u_canvasSize;       // Canvas size in physical pixels
  uniform vec2 u_offscreenSize;    // Offscreen buffer size in pixels
  uniform vec2 u_viewOffset;       // Pan offset in physical pixels
  uniform float u_scale;           // Zoom scale
  uniform vec2 u_tileOffset;       // First visible tile coordinates
  uniform vec2 u_tileSize;         // Tile size in physical pixels (16*dpr, 24*dpr)
  uniform vec2 u_padding;          // Padding in pixels added to offscreen buffer
  uniform float u_aaEnabled;       // 1.0 = AA on, 0.0 = AA off
  uniform vec2 u_baseTileSize;     // Base tile size (16, 24) for coordinate conversion

  in vec2 v_screenPos;  // 0-1 normalized screen position

  out vec4 fragColor;

  void main() {
    // Convert normalized screen pos to physical pixels
    vec2 screenPos = v_screenPos * u_canvasSize;

    // Calculate position in map tile coordinates
    // viewOffset is where tile (0,0) would appear on screen
    vec2 mapPos = (screenPos - u_viewOffset) / (u_tileSize * u_scale);

    // Position relative to the first visible tile (which is at offscreen padding)
    vec2 relativeMapPos = mapPos - u_tileOffset;

    // Convert to offscreen buffer pixels (at 1x scale, using base tile size)
    // Add padding offset since sprites are rendered with padding border
    vec2 offscreenPos = relativeMapPos * u_baseTileSize + u_padding;

    // Calculate UV (flip Y because WebGL textures have origin at bottom-left)
    vec2 uv = offscreenPos / u_offscreenSize;
    uv.y = 1.0 - uv.y;

    // Pixel art AA - smooth edges when scaling up (when enabled)
    vec2 finalUV = uv;
    if (u_aaEnabled > 0.5) {
      vec2 pix = uv * u_offscreenSize;
      pix = floor(pix) + min(fract(pix) / fwidth(pix), 1.0) - 0.5;
      finalUV = pix / u_offscreenSize;
    }

    fragColor = texture(u_offscreenTex, finalUV);
  }
`;

/** Sprite pass attribute locations */
export interface SpriteAttribs {
  position: number;
  tilePos: number;
  spriteUV: number;
  fgColor: number;
  detailColor: number;
  atlasLayer: number;
}

/** Sprite pass uniform locations */
export interface SpriteUniforms {
  resolution: WebGLUniformLocation | null;
  tileSize: WebGLUniformLocation | null;
  tileOffset: WebGLUniformLocation | null;
  atlasArray: WebGLUniformLocation | null;
  atlasArraySize: WebGLUniformLocation | null;
  pixelOffset: WebGLUniformLocation | null;
}

/** Compose pass attribute locations */
export interface ComposeAttribs {
  position: number;
}

/** Compose pass uniform locations */
export interface ComposeUniforms {
  canvasSize: WebGLUniformLocation | null;
  offscreenSize: WebGLUniformLocation | null;
  viewOffset: WebGLUniformLocation | null;
  scale: WebGLUniformLocation | null;
  tileOffset: WebGLUniformLocation | null;
  tileSize: WebGLUniformLocation | null;
  padding: WebGLUniformLocation | null;
  offscreenTex: WebGLUniformLocation | null;
  aaEnabled: WebGLUniformLocation | null;
  baseTileSize: WebGLUniformLocation | null;
}

export class TileMapShader {
  readonly spriteProgram: WebGLProgram;
  readonly composeProgram: WebGLProgram;

  readonly spriteAttribs: SpriteAttribs;
  readonly spriteUniforms: SpriteUniforms;
  readonly composeAttribs: ComposeAttribs;
  readonly composeUniforms: ComposeUniforms;

  // Shared quad buffer (unit quad for both passes)
  readonly quadBuffer: WebGLBuffer;

  private gl: WebGL2RenderingContext;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;

    // Compile sprite pass shaders
    const spriteVert = this.compileShader(gl.VERTEX_SHADER, SPRITE_VERTEX_SHADER);
    const spriteFrag = this.compileShader(gl.FRAGMENT_SHADER, SPRITE_FRAGMENT_SHADER);

    this.spriteProgram = gl.createProgram()!;
    gl.attachShader(this.spriteProgram, spriteVert);
    gl.attachShader(this.spriteProgram, spriteFrag);
    gl.linkProgram(this.spriteProgram);

    if (!gl.getProgramParameter(this.spriteProgram, gl.LINK_STATUS)) {
      throw new Error('Sprite shader link failed: ' + gl.getProgramInfoLog(this.spriteProgram));
    }

    // Compile compose pass shaders
    const composeVert = this.compileShader(gl.VERTEX_SHADER, COMPOSE_VERTEX_SHADER);
    const composeFrag = this.compileShader(gl.FRAGMENT_SHADER, COMPOSE_FRAGMENT_SHADER);

    this.composeProgram = gl.createProgram()!;
    gl.attachShader(this.composeProgram, composeVert);
    gl.attachShader(this.composeProgram, composeFrag);
    gl.linkProgram(this.composeProgram);

    if (!gl.getProgramParameter(this.composeProgram, gl.LINK_STATUS)) {
      throw new Error('Compose shader link failed: ' + gl.getProgramInfoLog(this.composeProgram));
    }

    // Get sprite pass locations
    this.spriteAttribs = {
      position: gl.getAttribLocation(this.spriteProgram, 'a_position'),
      tilePos: gl.getAttribLocation(this.spriteProgram, 'a_tilePos'),
      spriteUV: gl.getAttribLocation(this.spriteProgram, 'a_spriteUV'),
      fgColor: gl.getAttribLocation(this.spriteProgram, 'a_fgColor'),
      detailColor: gl.getAttribLocation(this.spriteProgram, 'a_detailColor'),
      atlasLayer: gl.getAttribLocation(this.spriteProgram, 'a_atlasLayer'),
    };

    this.spriteUniforms = {
      resolution: gl.getUniformLocation(this.spriteProgram, 'u_resolution'),
      tileSize: gl.getUniformLocation(this.spriteProgram, 'u_tileSize'),
      tileOffset: gl.getUniformLocation(this.spriteProgram, 'u_tileOffset'),
      atlasArray: gl.getUniformLocation(this.spriteProgram, 'u_atlasArray'),
      atlasArraySize: gl.getUniformLocation(this.spriteProgram, 'u_atlasArraySize'),
      pixelOffset: gl.getUniformLocation(this.spriteProgram, 'u_pixelOffset'),
    };

    // Get compose pass locations
    this.composeAttribs = {
      position: gl.getAttribLocation(this.composeProgram, 'a_position'),
    };

    this.composeUniforms = {
      canvasSize: gl.getUniformLocation(this.composeProgram, 'u_canvasSize'),
      offscreenSize: gl.getUniformLocation(this.composeProgram, 'u_offscreenSize'),
      viewOffset: gl.getUniformLocation(this.composeProgram, 'u_viewOffset'),
      scale: gl.getUniformLocation(this.composeProgram, 'u_scale'),
      tileOffset: gl.getUniformLocation(this.composeProgram, 'u_tileOffset'),
      tileSize: gl.getUniformLocation(this.composeProgram, 'u_tileSize'),
      padding: gl.getUniformLocation(this.composeProgram, 'u_padding'),
      offscreenTex: gl.getUniformLocation(this.composeProgram, 'u_offscreenTex'),
      aaEnabled: gl.getUniformLocation(this.composeProgram, 'u_aaEnabled'),
      baseTileSize: gl.getUniformLocation(this.composeProgram, 'u_baseTileSize'),
    };

    // Set atlas sampler uniform
    gl.useProgram(this.spriteProgram);
    gl.uniform1i(this.spriteUniforms.atlasArray, 0);

    // Create shared quad buffer
    this.quadBuffer = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
    // prettier-ignore
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      0, 0,  1, 0,  0, 1,
      0, 1,  1, 0,  1, 1,
    ]), gl.STATIC_DRAW);
  }

  /**
   * Use the sprite program.
   */
  useSpriteProgram(): void {
    this.gl.useProgram(this.spriteProgram);
  }

  /**
   * Use the compose program.
   */
  useComposeProgram(): void {
    this.gl.useProgram(this.composeProgram);
  }

  /**
   * Dispose of shader resources.
   */
  dispose(): void {
    const gl = this.gl;
    gl.deleteProgram(this.spriteProgram);
    gl.deleteProgram(this.composeProgram);
    gl.deleteBuffer(this.quadBuffer);
  }

  private compileShader(type: number, source: string): WebGLShader {
    const gl = this.gl;
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const info = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error('Shader compile error: ' + info);
    }

    return shader;
  }
}
